import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AccountSummary from './components/AccountSummary';
import TransactionLedger from './components/TransactionLedger';
import ReconciliationPanel from './components/ReconciliationPanel';
import { accountService } from '../../services/accountService';
import { transactionService } from '../../services/transactionService';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/currency';

const AccountDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const accountFromState = location?.state?.account;
  const createTransaction = location?.state?.createTransaction;
  const billData = location?.state?.billData;

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [preFillData, setPreFillData] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && id) {
      if (id === ':id' || !id || id?.trim() === '') {
        setError('Invalid account ID. Please select an account from the accounts overview page.');
        setLoading(false);
        return;
      }
      loadAccountDetails();
      loadTransactions();
    } else if (user && !id) {
      setError('No account ID provided. Please select an account from the accounts overview page.');
      setLoading(false);
    }
  }, [user, id]);

  // Handle bill data from navigation state
  useEffect(() => {
    if (createTransaction && billData) {
      setPreFillData(billData);
    }
  }, [createTransaction, billData]);

  const loadAccountDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await accountService?.getById(id);
      setAccount(data);
    } catch (err) {
      console.error('Error loading account details:', err);
      setError(err?.message || 'Failed to load account details');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const data = await transactionService?.getByAccount(id);
      setTransactions(data || []);
    } catch (err) {
      console.error('Error loading transactions:', err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const [showReconciliation, setShowReconciliation] = useState(false);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Accounts', path: '/accounts-overview' },
    { label: account?.name || 'Account Details', path: '/account-details' }
  ];

  const handleTransactionUpdate = async (updatedTransaction) => {
    try {
      await transactionService?.update(updatedTransaction?.id, {
        date: updatedTransaction?.date,
        description: updatedTransaction?.description,
        amount: updatedTransaction?.amount,
        type: updatedTransaction?.type,
        categoryId: updatedTransaction?.categoryId,
        cleared: updatedTransaction?.cleared
      });
      
      setTransactions(prev => 
        prev?.map(t => t?.id === updatedTransaction?.id ? { ...t, ...updatedTransaction } : t)
      );
      
      if (updatedTransaction?.amount !== undefined) {
        await loadAccountDetails();
      }
    } catch (err) {
      console.error('Error updating transaction:', err);
      alert(err?.message || 'Failed to update transaction');
    }
  };

  const handleTransactionDelete = async (transactionId) => {
    if (!confirm('Are you sure you want to delete this transaction?')) return;
    
    try {
      await transactionService?.delete(transactionId);
      setTransactions(prev => prev?.filter(t => t?.id !== transactionId));
      await loadAccountDetails();
    } catch (err) {
      console.error('Error deleting transaction:', err);
      alert(err?.message || 'Failed to delete transaction');
    }
  };

  const handleTransactionAdd = async (newTransaction) => {
    try {
      const created = await transactionService?.create({
        accountId: id,
        ...newTransaction
      });
      
      setTransactions(prev => [created, ...prev]);
      await loadAccountDetails();
    } catch (err) {
      console.error('Error adding transaction:', err);
      alert(err?.message || 'Failed to add transaction');
    }
  };

  const handleReconciliationComplete = async (reconciledData) => {
    try {
      // Handle new transaction if provided
      if (reconciledData?.newTransaction) {
        await handleTransactionAdd(reconciledData?.newTransaction);
      }
      
      // Handle adjustment transaction if provided
      if (reconciledData?.adjustmentTransaction) {
        await handleTransactionAdd(reconciledData?.adjustmentTransaction);
      }

      // Update cleared status for all transactions
      const updatePromises = reconciledData?.clearedTransactionIds?.map(transactionId => 
        transactionService?.update(transactionId, { cleared: true })
      );
      await Promise.all(updatePromises);

      // Reload data to reflect changes
      await loadAccountDetails();
      await loadTransactions();
      
      setShowReconciliation(false);
    } catch (err) {
      console.error('Error completing reconciliation:', err);
      alert(err?.message || 'Failed to complete reconciliation');
    }
  };

  const reconciliationStatus = {
    isBalanced: transactions?.filter(t => !t?.cleared)?.length === 0,
    unclearedCount: transactions?.filter(t => !t?.cleared)?.length,
    lastReconciled: account?.lastReconciled
  };

  return (
    <>
      <Helmet>
        <title>Account Details - {account?.name} - Rovyn Budget</title>
        <meta name="description" content="Comprehensive account management with running balance ledger, transaction history, and reconciliation tools" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="mx-auto px-16 md:px-24 py-24 md:py-32 max-w-[1600px]">
            <Breadcrumbs items={breadcrumbItems} />

            {loading && (
              <div className="flex items-center justify-center py-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-16"></div>
                  <p className="text-muted-foreground">Loading account details...</p>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-error/10 border border-error rounded-lg p-20 mb-24">
                <div className="flex items-start gap-12">
                  <Icon name="AlertCircle" size={24} color="var(--color-error)" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-error mb-4">Error Loading Account</h3>
                    <p className="text-sm text-error/80 mb-16">{error}</p>
                    <div className="flex gap-12">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/accounts-overview')}
                      >
                        Go to Accounts Overview
                      </Button>
                      {id && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            setError('');
                            loadAccountDetails();
                          }}
                        >
                          Retry
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && account && (
              <>
            <div className="flex items-center justify-between flex-wrap gap-16 mb-24 md:mb-32">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground mb-8">
                  {account?.name}
                </h1>
                <p className="text-sm md:text-base text-muted-foreground font-caption">
                  Complete transaction history with running balance and reconciliation tools
                </p>
              </div>
              <div className="flex gap-12">
                <Button
                  variant="outline"
                  iconName="FileDown"
                  iconPosition="left"
                  iconSize={18}
                  onClick={() => {
                    const csvContent = [
                      ['Date', 'Description', 'Amount', 'Category', 'Cleared'],
                      ...transactions?.map(t => [
                        new Date(t?.date)?.toLocaleDateString(),
                        t?.description,
                        formatCurrency(t?.amount),
                        t?.category,
                        t?.cleared ? 'Yes' : 'No'
                      ])
                    ]?.map(row => row?.join(','))?.join('\n');
                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = window.URL?.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${account?.name}-transactions.csv`;
                    link?.click();
                    window.URL?.revokeObjectURL(url);
                  }}
                >
                  Export CSV
                </Button>
                <Button
                  variant={showReconciliation ? 'outline' : 'default'}
                  iconName="CheckCircle2"
                  iconPosition="left"
                  iconSize={18}
                  onClick={() => setShowReconciliation(!showReconciliation)}
                >
                  {showReconciliation ? 'Close Reconciliation' : 'Reconcile Account'}
                </Button>
              </div>
            </div>

            <AccountSummary 
              account={account}
              reconciliationStatus={reconciliationStatus}
              onEdit={() => {}}
              onDelete={() => {}}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-24 mt-32">
              <div className="lg:col-span-2">
                <TransactionLedger
                  transactions={transactions}
                  currency={account?.currency}
                  accountBalance={account?.balance}
                  account={account}
                  onTransactionUpdate={handleTransactionUpdate}
                  onTransactionDelete={handleTransactionDelete}
                  onTransactionAdd={handleTransactionAdd}
                  preFillData={preFillData}
                  onPreFillDataUsed={() => setPreFillData(null)}
                />
              </div>
              
              {showReconciliation && (
                <div className="lg:col-span-1">
                  <ReconciliationPanel
                    transactions={transactions}
                    account={account}
                    onReconciliationComplete={handleReconciliationComplete}
                    onClose={() => setShowReconciliation(false)}
                  />
                </div>
              )}
            </div>
            </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AccountDetails;